import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { IInfo } from '@lib/interfaces/cash.interface';
import { AccountsService, SidePanelService } from '@lib/services';
import { AngularMaterialModule } from 'src/app/material.module';
import { ReceiptComponent } from '../receipt/receipt.component';
import { ApiResponseError, IAccount, IAuthResponse } from '@lib/interfaces';
import { CardLessService } from '@lib/services/card-less/card-less.service';
import { IVoucherListIndex, RedemptionChannel, VoucherOperationType } from '@lib/interfaces/card-less.interface';
import { BaseComponent } from '@lib/components';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize, forkJoin } from 'rxjs';
import { CustomersService } from '@lib/services/customer/customer.service';
import { SpinnerComponent } from '../../../../lib/components/spinner/spinner.component';
import { CashInComponent } from '../cash-in/cash-in.component';
import { CashOutComponent } from '../cash-out/cash-out.component';

type CashScreen = 'cash-in' | 'cash-out';

@Component({
    selector: 'app-confirmation',
    templateUrl: './confirmation.component.html',
    styleUrls: ['./confirmation.component.scss'],
    standalone: true,
    imports: [AngularMaterialModule, SpinnerComponent],
    providers: [SidePanelService],
})
export class ConfirmationComponent extends BaseComponent implements OnInit {
    public screen: CashScreen = 'cash-in';
    private userInfo: IAuthResponse['admin'] = JSON.parse(localStorage.getItem('userInfo') || '');
    public voucherInfo: IVoucherListIndex;
    public loading = false;
    protected countdown = '';
    protected timerRef: any;

    info: IInfo = {
        name: '',
        email: '',
        mobile: '',
        walletId: '',
        validity: '',
        cashAmount: 0,
        fee: 0,
        total: 0,
        currency: '',
        refId: '',
        date: new Date(),
        imgUrl: '',
    };

    constructor(
        public dialogRef: MatDialogRef<ConfirmationComponent>,
        private sidePanelService: SidePanelService,
        private cardLessService: CardLessService,
        private snackBarService: MatSnackBar,
        private accountService: AccountsService,
        private customerService: CustomersService,
        protected readonly sidePanel: SidePanelService,
    ) {
        super();
    }

    ngOnInit() {
        this.transformResponseToInfo();
    }

    transformResponseToInfo() {
        forkJoin([
            this.accountService.getAccountsByUserId(this.voucherInfo.user.id),
            this.customerService.filterCustomers({
                email: this.voucherInfo.user.email,
                name: this.voucherInfo.user.first_name,
            }),
        ]).subscribe({
            next: (responses) => {
                const account: IAccount | undefined = responses[0].data.find((account: IAccount) => {
                    return account.currencies.includes(this.voucherInfo.currency);
                });

                this.updateCountdown();
                this.timerRef = setInterval(() => this.updateCountdown(), 1000);

                const mobile = responses[1].data.find((customer) => customer.id === this.voucherInfo.user.id)?.mobile;

                this.info.name = this.voucherInfo.user.first_name + ' ' + this.voucherInfo.user.last_name;
                this.info.mobile = mobile || '';
                this.info.email = this.voucherInfo.user.email;
                this.info.cashAmount = this.voucherInfo.amount;
                this.info.fee = 0; // for now it is zero
                this.info.total = this.info.cashAmount + this.info.fee;
                this.info.date = new Date();
                this.info.currency = this.voucherInfo.currency;
                this.info.refId = this.voucherInfo.voucher_code;
                this.info.walletId = account ? ` ${'*'.repeat(7)}${account.id.slice(-4)}` : '';
                this.info.validity = this.voucherInfo.expiry_time;
            },
        });
    }

    updateCountdown(): void {
        const now = new Date().getTime();
        const targetDate = new Date(this.voucherInfo.expiry_time);
        const distance = targetDate.getTime() - now;

        if (distance <= 0) {
            this.countdown = 'exipred';
            clearInterval(this.timerRef);
        } else {
            const days = Math.floor(distance / (1000 * 60 * 60 * 24));
            const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((distance % (1000 * 60)) / 1000);

            this.countdown = '';

            if (days > 0) {
                this.countdown += `${days}d `;
            }
            if (hours > 0) {
                this.countdown += `${hours}h `;
            }
            if (minutes > 0) {
                this.countdown += `${minutes}m `;
            }
            if (seconds > 0) {
                this.countdown += `${seconds}s`;
            }
        }
    }

    onConfirm() {
        // fetch receiver's details form accountid
        this.loading = true;
        this.cardLessService
            .redeemVoucher(
                {
                    channel: RedemptionChannel.FIB,
                    operation_code: this.screen === 'cash-in' ? VoucherOperationType.INWARD : VoucherOperationType.OUTWARD,
                    agent_account_id: this.accountService.agentsAccounts().get(this.info.currency) || '',
                    amount: this.info.cashAmount,
                },
                this.voucherInfo.voucher_code,
            )
            .pipe(
                finalize(() => {
                    this.loading = false;
                }),
            )
            .subscribe({
                next: () => {
                    this.dialogRef.close();
                    const dialogRef = this.sidePanelService.open(ReceiptComponent);
                    dialogRef.componentInstance.info = this.info;
                    dialogRef.componentInstance.screen = this.screen;
                },
                error: (error: ApiResponseError) => {
                    if (error.status === 400 && error.error?.detail === 'Maximum account balance limit reached') {
                        this.dialogRef.close();
                        const dialogRef = this.sidePanelService.open(ReceiptComponent);
                        dialogRef.componentInstance.info = this.info;
                        dialogRef.componentInstance.screen = this.screen;
                        dialogRef.componentInstance.errorObj = {
                            error: 'Insufficient Funds',
                            message: `Unable to complete ${this.screen}. Please check your balance and submit a new request.`,
                        };
                        return;
                    }
                    this.createApiErrorSnackBar(this.snackBarService, error);
                },
            });
    }

    protected handleBack() {
        this.dialogRef.close();
        this.screen === 'cash-in' ? this.sidePanel.open(CashInComponent) : this.sidePanel.open(CashOutComponent);
    }
}
